import { AfterViewInit, Component, inject, Input, TemplateRef, ViewChild } from '@angular/core';
import { IonicModule, PopoverController } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';

import { Modal } from '../../../global/modal';
import { SettingsService } from '../../../global/settings';
import { Force } from '../../models';

import { DEFAULT_PDF_CONFIG, PDFConfig } from './pdf-config';
import { PDFService } from './pdf-service';
import { GAME_ID } from '../../../global/game-id';
import { getValueFromTranslationSync } from '../../../global/language/selectLanguage';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
    standalone: true,
    imports: [CommonModule, IonicModule, FormsModule, TranslateModule],
    selector: 'abs-pdf-settings',
    template: `
        <ng-template #pdfSettings>
            <ion-list *ngIf="pdfConfig">
                <ion-item>
                    <ion-label>{{ 'GLOBAL.PDF.INCLUDE_PAGE_NUMBERS' | translate }}</ion-label>
                    <ion-checkbox slot="end" [(ngModel)]="pdfConfig.includePageNumbers" (ngModelChange)="saveSettings()"></ion-checkbox>
                </ion-item>
                <ion-item>
                    <ion-label>{{ 'GLOBAL.PDF.BASE_FONT_SIZE' | translate }}</ion-label>
                    <ion-range
                        aria-label="Font Size"
                        [ticks]="true"
                        [step]="2"
                        [snaps]="true"
                        [min]="6"
                        [max]="14"
                        [(ngModel)]="pdfConfig.baseFontSize"
                        (ngModelChange)="saveSettings()"
                        [pin]="true"
                    >
                    </ion-range>
                </ion-item>
                <ion-item *ngIf="pdfConfig.useWebForPDF !== undefined">
                    <ion-label>
                        <div>{{ 'GLOBAL.PDF.USE_WEB_FOR_PDF' | translate }}</div>
                        <div class="ion-text-wrap" style="font-size: 0.85em; color: var(--ion-color-medium);">
                            Recommended when device font size is set larger than default
                        </div>
                    </ion-label>
                    <ion-checkbox slot="end" [(ngModel)]="pdfConfig.useWebForPDF" (ngModelChange)="saveSettings()"></ion-checkbox>
                </ion-item>
            </ion-list>
        </ng-template>

        <ng-template #pdfButtons>
            <ion-button>{{ 'GLOBAL.BUTTONS.CANCEL' | translate }}</ion-button>
            <ion-button (click)="this.exportPDF()">{{ 'GLOBAL.BUTTONS.EXPORT' | translate }}</ion-button>
        </ng-template>
    `,
    styles: [
        `
            ion-range {
                padding-top: 0;
            }
        `
    ]
})
export class PDFSettingsComponent implements AfterViewInit {
    pdfConfig: PDFConfig = { ...DEFAULT_PDF_CONFIG };
    pdfConfig$ = this.settingsService.settings$.pipe(
        map((s) => ({
            ...DEFAULT_PDF_CONFIG,
            ...(s[this.gameId]?.pdfConfig || {})
        }))
    );

    @ViewChild('pdfSettings')
    pdfSettings: TemplateRef<any>;

    @ViewChild('pdfButtons')
    pdfButtons: TemplateRef<any>;

    @Input()
    force: Force;

    private gameId: string = inject(GAME_ID);

    constructor(
        private modal: Modal,
        private popoverController: PopoverController,
        protected settingsService: SettingsService,
        private pdfService: PDFService,
        private translateService: TranslateService
    ) {}

    ngAfterViewInit(): void {
        this.pdfConfig$.subscribe((config) => {
            this.pdfConfig = config;
        });
    }

    public show(props: any = {}) {
        this.modal.show({
            props: {
                title: this.translateService.instant('GLOBAL.PDF.SETTINGS'),
                contentTemplate: this.pdfSettings,
                buttonsTemplate: this.pdfButtons,
                buttonsDirection: 'horizontal',
                ...props
            }
        });
    }

    saveSettings() {
        this.settingsService.updateValue(this.gameId, 'pdfConfig', this.pdfConfig);
    }

    exportPDF() {
        this.saveSettings();
        this.pdfService.generatePDF(this.force, this.pdfConfig);
    }

    dismiss() {
        this.popoverController.dismiss();
    }

    getFontSizeLabel(value: number) {
        const labels = {
            6: 'GLOBAL.PDF.FONTSIZE_VSMALL',
            8: 'GLOBAL.PDF.FONTSIZE_SMALL',
            10: 'GLOBAL.PDF.FONTSIZE_MED',
            12: 'GLOBAL.PDF.FONTSIZE_LARGE',
            14: 'GLOBAL.PDF.FONTSIZE_VLARGE'
        };
        const label = getValueFromTranslationSync(labels[value]);
        return label;
    }
}
